import {
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Rating,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ComponentErrorBoundary } from "core/components/ComponentErrorBoundary";
import { ErrorComponent } from "core/components/ErrorComponent";
import {
  GridAreaContainer,
  type GridAreaContainerProps,
} from "core/components/GridAreaContainer";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useFormatDate } from "core/hooks/useFormatDate";
import { useRelativeTimeFormatter } from "core/hooks/useRelativeTimeFormatter";
import { differenceInDays } from "date-fns";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { HorizontalScrollContainer } from "../components/HorizontalScrollContainer";

type RatingValue = 0 | 1 | 2 | 3 | 4 | 5;
type ReviewCardProps = {
  review: string | null | undefined;
  rating: RatingValue;
  shopName: string | null | undefined;
  date: Date | string | null | undefined;
  author: string | null | undefined;
  variant: "elevation" | "outlined";
  id: string;
  onClick?: (id: string) => void;
};

const GRID_AREA = "reviews";

function EmptyReviewCard() {
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:ReviewCard" });
  const noReviewsText = t("noReviews", "No reviews available");
  return (
    <Card sx={{ width: 325 }} variant="outlined">
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {noReviewsText}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ReviewCard({
  review,
  rating,
  shopName,
  date,
  author,
  variant,
  id,
  onClick,
}: ReviewCardProps) {
  const { t } = useTranslation("Fixhub", { keyPrefix: "Next:Core:ReviewCard" });
  const anonAuthorText = t("anonymousAuthor", "Anonymous");
  const noReviewText = t("noReview", "No review text");
  const noShopNameText = t("noShopName", "Unknown Shop");
  const formatRelativeDate = useRelativeTimeFormatter();
  const daysDifference = date
    ? differenceInDays(new Date(date), new Date())
    : 0;
  return (
    <Card sx={{ width: 325 }} variant={variant}>
      <CardActionArea onClick={() => onClick?.(id)}>
        <CardContent sx={{ containerType: "inline-size" }}>
          <Typography sx={{ fontWeight: 500 }}>
            {shopName || noShopNameText}
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">
            {author || anonAuthorText}
          </Typography>
          <Stack direction="row" alignItems="end" mb={1} gap={1}>
            <Rating name="read-only" value={rating} readOnly />
            {date && (
              <Typography variant="caption" color="text.secondary">
                {daysDifference === 0
                  ? t("today", "Today")
                  : formatRelativeDate(daysDifference, "day")}
              </Typography>
            )}
          </Stack>
          <Tooltip title={review} placement="bottom">
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
              }}
            >
              {review ? `"${review}"` : noReviewText}
            </Typography>
          </Tooltip>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function Wrapper({
  children,
  containerProps,
}: {
  children: React.ReactNode;
  containerProps?: GridAreaContainerProps;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ReviewsWidget",
  });
  return (
    <ComponentErrorBoundary fallBackBoxProps={{ sx: { gridArea: GRID_AREA } }}>
      <GridAreaContainer
        {...containerProps}
        gridArea={GRID_AREA}
        title={t("gridAreaContainerTitle", "Customer Reviews")}
      >
        {children}
      </GridAreaContainer>
    </ComponentErrorBoundary>
  );
}

export function ReviewsWidget() {
  return (
    <Wrapper>
      <ReviewCarrousel />
    </Wrapper>
  );
}

export function ReviewCarrousel() {
  const { data, isError, isLoading } = useCentrixApi({
    path: "/api/app/soci-reviews/review-feed",
  });
  const [openReviewId, setOpenReviewId] = useState<string | null>(null);
  const handleReviewClick = useCallback((id: string) => {
    setOpenReviewId(id);
  }, []);
  const openReview =
    data?.items?.find((item) => item.id === openReviewId) ?? null;
  if (isError) {
    return <ErrorComponent apiError={isError} />;
  }
  if (isLoading || !data) {
    return (
      <HorizontalScrollContainer>
        <Skeleton variant="rounded" width={300} height={200} />
        <Skeleton variant="rounded" width={300} height={200} />
        <Skeleton variant="rounded" width={300} height={200} />
        <Skeleton variant="rounded" width={300} height={200} />
        <Skeleton variant="rounded" width={300} height={200} />
      </HorizontalScrollContainer>
    );
  }
  if (!Array.isArray(data.items) || data.items.length === 0) {
    return (
      <HorizontalScrollContainer>
        <EmptyReviewCard />
      </HorizontalScrollContainer>
    );
  }

  return (
    <>
      <HorizontalScrollContainer>
        {data.items.map((item, i) => (
          <ReviewCard
            key={item.id ?? i}
            id={item.id ?? `review-${i}`}
            review={item.review}
            author={item.authorName}
            rating={(item.rating || 0) as RatingValue}
            shopName={item.shopName}
            date={item.date}
            variant="elevation"
            onClick={handleReviewClick}
          />
        ))}
      </HorizontalScrollContainer>
      <ReviewDetailsDialog
        open={!!openReview}
        review={openReview?.review}
        rating={(openReview?.rating || 0) as RatingValue}
        shopName={openReview?.shopName}
        date={openReview?.date}
        author={openReview?.authorName}
        onClose={() => setOpenReviewId(null)}
      />
    </>
  );
}

function ReviewDetailsDialog({
  open: openProp,
  review,
  rating,
  shopName,
  date,
  author,
  onClose,
}: {
  open: boolean;
  review: string | null | undefined;
  rating: RatingValue;
  shopName: string | null | undefined;
  date: Date | string | null | undefined;
  author: string | null | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ReviewDetailsDialog",
  });
  const noReviewText = t("noReview", "No review text");
  const noShopNameText = t("noShopName", "Unknown Shop");
  const anonAuthorText = t("anonymousAuthor", "Anonymous");
  const postedByLabel = t("postedBy", "Posted By:");
  const postedOnLabel = t("postedOn", "Posted On:");
  const formatDate = useFormatDate();
  const formatRelativeDate = useRelativeTimeFormatter();
  const daysDifference = date
    ? differenceInDays(new Date(date), new Date())
    : null;

  return (
    <Dialog open={openProp} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{shopName || noShopNameText}</DialogTitle>

      <DialogContent>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle2">
          {postedByLabel} {author || anonAuthorText}
        </Typography>

        {date && daysDifference !== null && (
          <Typography variant="subtitle2" gutterBottom>
            {postedOnLabel} {formatDate(date)} (
            {daysDifference === 0
              ? t("today", "Today")
              : formatRelativeDate(daysDifference, "day")}
            )
          </Typography>
        )}
        <div>
          <Rating name="read-only" value={rating} readOnly />
        </div>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          {review ? `"${review}"` : noReviewText}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
